/**
 * STEP_HELP_COPY & FIELD_HELP_COPY
 *
 * Centralized copy dictionary for applicant workflow help tooltips, modal explanations,
 * and step completion encouragement messages.
 *
 * All sensitive identity/PII fields cite AES-256-GCM encryption at rest to maintain trust.
 */

export interface StepHelpItem {
  id: string;
  stepNum: number;
  title: string;
  explanation: string;
  encouragementTitle: string;
  encouragementMessage: string;
  nextStepTitle?: string;
}

export interface FieldHelpItem {
  fieldKey: string;
  title: string;
  explanation: string;
}

export const STEP_HELP_COPY: Record<number, StepHelpItem> = {
  1: {
    id: 'personal',
    stepNum: 1,
    title: 'Personal Details',
    explanation: 'We collect your full legal name, date of birth, and contact details to verify your identity and ensure your application matches official civil records. Your sensitive personal data is strictly encrypted at rest using AES-256-GCM.',
    encouragementTitle: 'Personal Info Saved!',
    encouragementMessage: 'Got it — your personal details are saved. Next, let\'s cover your passport and immigration information.',
    nextStepTitle: 'Passport Details',
  },
  2: {
    id: 'passport',
    stepNum: 2,
    title: 'Passport & Immigration',
    explanation: 'We collect your passport details to verify your identity for visa, sponsor, and immigration processing. Passport details are encrypted at rest and shared only with verified program sponsors and immigration authorities.',
    encouragementTitle: 'Passport Saved!',
    encouragementMessage: 'Great work — your passport details are secured. Next, let\'s add your educational background.',
    nextStepTitle: 'Educational Background',
  },
  3: {
    id: 'education',
    stepNum: 3,
    title: 'Education Background',
    explanation: 'Educational background helps sponsors and employers confirm your academic eligibility and qualification level for exchange programs and placements.',
    encouragementTitle: 'Education Details Recorded!',
    encouragementMessage: 'Awesome — your academic history is logged. Next, let\'s walk through your work experience.',
    nextStepTitle: 'Work Experience',
  },
  4: {
    id: 'work',
    stepNum: 4,
    title: 'Work Experience',
    explanation: 'Your work experience enables matching with appropriate host organizations, internship positions, and career opportunities aligned with your background.',
    encouragementTitle: 'Work History Saved!',
    encouragementMessage: 'Well done — your employment background is saved. Next, let\'s add your emergency contact contacts.',
    nextStepTitle: 'Emergency Contacts',
  },
  5: {
    id: 'contacts',
    stepNum: 5,
    title: 'Emergency Contacts',
    explanation: 'Emergency contacts ensure program administrators and sponsors can reach designated family members or legal guardians promptly during travel or emergencies.',
    encouragementTitle: 'Contacts Saved!',
    encouragementMessage: 'Got it — emergency contact details are locked in. Next, let\'s set your program preferences.',
    nextStepTitle: 'Program Preferences',
  },
  6: {
    id: 'preferences',
    stepNum: 6,
    title: 'Program Preferences',
    explanation: 'Your location and industry preferences assist placement officers in matching you with opportunities that fit your career goals and timing.',
    encouragementTitle: 'Preferences Saved!',
    encouragementMessage: 'Fantastic — your placement preferences are set. Next, let\'s upload required documents.',
    nextStepTitle: 'Document Uploads',
  },
  7: {
    id: 'documents',
    stepNum: 7,
    title: 'Document Uploads',
    explanation: 'Document uploads (resume, passport copy, transcripts) are required by program sponsors to confirm your application file and issue visa sponsorship forms.',
    encouragementTitle: 'Documents Recorded!',
    encouragementMessage: 'Excellent — your supporting documents are uploaded. Next, let\'s review your complete application.',
    nextStepTitle: 'Application Review',
  },
  8: {
    id: 'review',
    stepNum: 8,
    title: 'Application Review',
    explanation: 'Reviewing your application gives you a complete summary of all submitted details prior to legal declaration and final submission.',
    encouragementTitle: 'Review Completed!',
    encouragementMessage: 'Everything looks clear — you\'ve reviewed your entire application. Finally, sign the legal declaration.',
    nextStepTitle: 'Declaration & Sign',
  },
  9: {
    id: 'declaration',
    stepNum: 9,
    title: 'Declaration & Sign',
    explanation: 'Your legal declaration confirms that all information provided is accurate and truthful, establishing your official application submission.',
    encouragementTitle: 'Application Completed!',
    encouragementMessage: 'Congratulations — your application declaration is complete and ready for review!',
    nextStepTitle: 'Submit',
  },
};

export const FIELD_HELP_COPY: Record<string, FieldHelpItem> = {
  firstName: {
    fieldKey: 'firstName',
    title: 'First Given Name',
    explanation: 'Your given first name as listed on official government identity documents. Used for all program communications and visa paperwork.',
  },
  lastName: {
    fieldKey: 'lastName',
    title: 'Last Family Name',
    explanation: 'Your legal surname/family name. Must match civil records and passport details exactly.',
  },
  passportNumber: {
    fieldKey: 'passportNumber',
    title: 'Passport Number',
    explanation: 'Your passport number is required for visa eligibility checks and travel document verification. It is stored using military-grade AES-256-GCM encryption.',
  },
  passportCountry: {
    fieldKey: 'passportCountry',
    title: 'Passport Issuing Country',
    explanation: 'The country that issued your current official passport. Used to determine international visa processing requirements.',
  },
  passportDates: {
    fieldKey: 'passportDates',
    title: 'Passport Issue & Expiry Dates',
    explanation: 'Passports typically must be valid for at least 6 months beyond your intended stay in the host country for visa approval.',
  },
  dob: {
    fieldKey: 'dob',
    title: 'Date of Birth',
    explanation: 'Your date of birth is used to verify age eligibility for program regulations and health insurance coverage. Stored with full encryption.',
  },
  gender: {
    fieldKey: 'gender',
    title: 'Gender',
    explanation: 'Required for consular visa applications, SEVIS reporting, and host accommodation arrangements.',
  },
  countryOfBirth: {
    fieldKey: 'countryOfBirth',
    title: 'Country of Birth',
    explanation: 'Required by immigration authorities and embassy consular staff for background verification and DS-2019 issuance.',
  },
  countryOfCitizenship: {
    fieldKey: 'countryOfCitizenship',
    title: 'Country of Citizenship',
    explanation: 'Determines visa category eligibility, reciprocity fee structures, and bilateral exchange agreements.',
  },
  nationalId: {
    fieldKey: 'nationalId',
    title: 'National ID / SSN',
    explanation: 'If provided, your national identification is used exclusively for tax, background check, or legal compliance by approved sponsors. Fully encrypted at rest.',
  },
  phone: {
    fieldKey: 'phone',
    title: 'Phone Number',
    explanation: 'We use your phone number for important application updates and emergency communications. We do not currently send SMS text alerts — application updates are sent by email.',
  },
  email: {
    fieldKey: 'email',
    title: 'Email Address',
    explanation: 'Your primary contact email for receiving official interview invitations, visa updates, and account security notifications.',
  },
  fullNameLegal: {
    fieldKey: 'fullNameLegal',
    title: 'Full Legal Name',
    explanation: 'Must match your government passport exactly to avoid visa issuance errors and customs verification delays.',
  },
  emergencyContact: {
    fieldKey: 'emergencyContact',
    title: 'Emergency Contact Information',
    explanation: 'Designated family member or legal guardian whom program officers can reach promptly during travel or health emergencies.',
  },
  programPreferences: {
    fieldKey: 'programPreferences',
    title: 'Program & Placement Preferences',
    explanation: 'Helps placement officers match your background, preferred locations, and target start dates with suitable host organizations.',
  },
  criminalHealth: {
    fieldKey: 'criminalHealth',
    title: 'Criminal & Health Background',
    explanation: 'Standard background declaration required by program sponsors and visa authorities for exchange visitor compliance.',
  },
  requiredDocuments: {
    fieldKey: 'requiredDocuments',
    title: 'Required Supporting Documents',
    explanation: 'Program sponsors and consular officers require official document uploads (passport, resume, transcripts) to verify eligibility.',
  },
  declaration: {
    fieldKey: 'declaration',
    title: 'Legal Declaration & Signature',
    explanation: 'Your electronic signature certifies under penalty of perjury that all information supplied is truthful and complete.',
  },
};
